// ============================================================
//  useLiveChatPulse — runs everywhere in the dashboard (AppLayout)
//
//  • keeps the owner "Online" for their website chat while CommandKit is open
//  • feeds the unread badge on the Website Widget menu item + "(2)" in the tab title
//  • plays a soft sound when a visitor sends a new message (switchable)
//  • with push notifications on (PushBell), the system notification takes over
//    whenever CommandKit isn't the window in front — no double sounds
//
//  The Chats screen polls faster and shares what it sees through the
//  'ck:lc-sync' window event, so the sound plays straight away there too.
// ============================================================
import { useEffect, useRef, useState } from 'react';
import { liveChatPresence } from '../../api/client';
import { pushFlagOn } from './livePush';

export const LC_SOUND_KEY = 'ck_lc_sound';

export function lcSoundOn() {
  try { return localStorage.getItem(LC_SOUND_KEY) !== '0'; } catch { return true; }
}
export function setLcSound(on) {
  try { localStorage.setItem(LC_SOUND_KEY, on ? '1' : '0'); } catch { /* ignore */ }
}

let audioCtx = null;
function unlock() {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === 'suspended') audioCtx.resume();
  } catch { audioCtx = null; }
}
/** A short two-note chime made on the fly — no sound file to ship. */
export function playChime() {
  unlock();
  if (!audioCtx) return;
  try {
    const t0 = audioCtx.currentTime;
    [[660, 0], [990, 0.12], [1320, 0.24]].forEach(([f, at]) => {
      const o = audioCtx.createOscillator();
      const g = audioCtx.createGain();
      o.type = 'sine'; o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t0 + at);
      g.gain.exponentialRampToValueAtTime(0.2, t0 + at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + 0.25);
      o.connect(g); g.connect(audioCtx.destination);
      o.start(t0 + at); o.stop(t0 + at + 0.3);
    });
  } catch { /* sound is a nicety */ }
}

/**
 * Show a desktop notification for a new visitor message (chat app only).
 * Uses the chat app's service worker when there is one, so clicks reopen the installed app window.
 */
async function notifyNew(latest) {
  if (!latest || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const title = `${latest.name} · Website chat`;
  const opts = { body: latest.text || 'New message', tag: `ck-lc-${latest.chatId}`, renotify: true, icon: '/chats-icon-192.png', badge: '/chats-icon-192.png', data: { chatId: latest.chatId } };
  try {
    const reg = navigator.serviceWorker && await navigator.serviceWorker.getRegistration('/chats');
    if (reg) { await reg.showNotification(title, opts); return; }
  } catch { /* fall back to a page notification */ }
  try {
    const n = new Notification(title, opts);
    n.onclick = () => { window.focus(); window.dispatchEvent(new CustomEvent('ck:lc-open', { detail: { chatId: latest.chatId } })); n.close(); };
  } catch { /* some browsers only allow notifications from a service worker */ }
}

export default function useLiveChatPulse({ notify = false, badge = false } = {}) {
  const [unread, setUnread] = useState(0);
  const lastIn = useRef(null);
  const optsRef = useRef({ notify, badge });
  optsRef.current = { notify, badge };

  useEffect(() => {
    let alive = true;
    let timer;
    // Browsers only allow sound after the user has interacted with the page once.
    const firstTouch = () => unlock();
    window.addEventListener('pointerdown', firstTouch, { once: true });

    const handle = d => {
      if (!d || !alive) return;
      if (typeof d.unreadChats === 'number') setUnread(d.unreadChats);
      if (typeof d.lastIn === 'number') {
        if (lastIn.current !== null && d.lastIn > lastIn.current) {
          const inFront = !document.hidden && document.hasFocus();
          // With push on, the system notification (and its sound) covers the times you're not looking.
          const pushCovers = pushFlagOn() && !inFront;
          if (lcSoundOn() && !pushCovers) playChime();
          // Desktop notification when the chat app isn't the window in front
          if (optsRef.current.notify && !inFront && !pushCovers && d.latest) notifyNew(d.latest);
        }
        lastIn.current = Math.max(lastIn.current ?? 0, d.lastIn);
      }
    };
    let inFlight = false;
    const tick = async () => {
      if (inFlight) return;
      inFlight = true;
      clearTimeout(timer);
      let next = 20000;
      try {
        const res = await liveChatPresence();
        handle(res.data);
        if (!res.data?.hasWidgets) next = 120000;
      } catch { next = 60000; }
      inFlight = false;
      if (alive) timer = setTimeout(tick, next);
    };
    tick();
    const onSync = e => handle(e.detail);
    window.addEventListener('ck:lc-sync', onSync);
    // A push arrived while you're looking at CommandKit → check right away (the service worker skipped the popup)
    let lastPush = 0;
    const onSw = e => {
      if (e.data?.type !== 'ck-lc-push' || Date.now() - lastPush < 1500) return;
      lastPush = Date.now();
      clearTimeout(timer);
      tick();
    };
    navigator.serviceWorker?.addEventListener('message', onSw);
    return () => {
      alive = false;
      clearTimeout(timer);
      navigator.serviceWorker?.removeEventListener('message', onSw);
      window.removeEventListener('ck:lc-sync', onSync);
      window.removeEventListener('pointerdown', firstTouch);
    };
  }, []);

  // "(2) CommandKit" in the browser tab while chats are waiting (+ the count on the installed app's icon)
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s/, '');
    document.title = unread > 0 ? `(${unread}) ${base}` : base;
    if (optsRef.current.badge && 'setAppBadge' in navigator) {
      (unread > 0 ? navigator.setAppBadge(unread) : navigator.clearAppBadge()).catch(() => {});
    }
  }, [unread]);

  return { unread };
}
